/**
 * Rutebeskyttelse i fast rækkefølge:
 * 1) ikke logget ind → /login
 * 2) MFA påkrævet → MFA-verifikation
 * 3) must_change_password → /skift-adgangskode
 * 4) profilstatus pending/inactive/suspended → statusskærm
 * 5) rollekrav (salesperson/superadmin)
 */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { FullPageSpinner } from "@/components/layout/FullPageSpinner";
import { StatusScreen } from "@/features/auth/components/StatusScreen";
import { MFAGate } from "@/features/auth/components/MFAGate";

interface Props {
  children: ReactNode;
  requireSalesperson?: boolean;
  requireSuperAdmin?: boolean;
}

export function ProtectedRoute({ children, requireSalesperson, requireSuperAdmin }: Props) {
  const { user, profile, loading, profileLoading, mfaRequired, isSalesperson, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (loading || (user && profileLoading && !profile)) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (mfaRequired) return <MFAGate />;
  if (profile?.must_change_password && location.pathname !== "/skift-adgangskode") {
    return <Navigate to="/skift-adgangskode" replace />;
  }
  if (profile && profile.status !== "active") return <StatusScreen status={profile.status} />;
  if (requireSuperAdmin && !isSuperAdmin) return <Navigate to="/" replace />;
  if (requireSalesperson && !isSalesperson) return <Navigate to="/" replace />;
  return <>{children}</>;
}
